// Helpers for the canvas-to-kernels save flow.
//
// The actual orchestration (one kernel-save sub-agent per canvas item)
// lives in ChatConsole — it owns the per-item kernel chat-item lifecycle
// and reuses kickOffKernelSave's existing UI for each save. This module
// just exposes the per-item derivation logic so the names + hints stay
// consistent with what the sub-agent will see in its task prompt.
//
// Per canvas item, the caller picks:
//   • command    — slash-command name (kebab-case, uniqued against the
//                   kernel store + the batch's other commands)
//   • title      — short human label
//   • description — falls back to the item's caption
//   • provenanceHint — task-prompt block telling the sub-agent how to
//                   identify the producing chain (deterministic when
//                   sourceToolCallIds was tagged at pin time;
//                   heuristic fallback when not)
//   • sourceToolCallIds — the same ids as data, used to scope the
//                   transcript window the sub-agent reads

import { sanitizeCommand, findKernelInCatalogByCommand } from "./kernelStore.js";

// Build the per-item batch entry: command, title, description, and
// provenance hint. Pass the full items array so sibling-item summaries
// can be inlined into each provenance hint — that's what stops two
// parallel sub-agents from claiming the same transcript chain when
// they shouldn't.
export function prepareCanvasItemBatch(items, tenantId = null) {
    const used = new Set();
    return items.map((item, idx) => {
        // Re-save path: if the item was already saved to a kernel,
        // reuse the same command so the new save overwrites the
        // existing kernel rather than producing a `-2` suffix. The
        // uniqueCommand check is skipped because the command is, by
        // definition, already in the store and pointing at this item.
        let command;
        if (item?.savedKernel?.command) {
            command = item.savedKernel.command;
        } else {
            const baseCommand = sanitizeCommand(
                deriveCommand(item) || `canvas-${item?.spec?.kind || "item"}-${idx + 1}`,
            );
            command = uniqueCommand(baseCommand, used, tenantId);
        }
        used.add(command);
        return {
            item,
            idx,
            command,
            title: deriveTitle(item, idx),
            description: item.caption || null,
            provenanceHint: buildProvenanceHint(item, items),
            // The tool calls that produced THIS item, normalized to an array.
            // The save agent anchors its transcript window on them, so an
            // untagged item must yield [] (widen to the analysis history) and
            // never undefined (which means "not a canvas item at all", and
            // selects the most recent window - the wrong one for every panel
            // but the newest).
            sourceToolCallIds: Array.isArray(item?.sourceToolCallIds)
                ? item.sourceToolCallIds
                : [],
        };
    });
}

// Build the Provenance hint block for a single canvas item. Two paths:
//   • Agent tagged sourceToolCallIds → list them as the starting point,
//     remind the sub-agent to verify (the tag might point to a failed
//     retry) and to still run the standard transformations.
//   • Untagged → tell the sub-agent to scan the transcript and identify
//     the chain by matching the spec's `kind` and any field references
//     against tool result shapes.
export function buildProvenanceHint(item, allItems = []) {
    const lines = [];
    const ids = Array.isArray(item.sourceToolCallIds) ? item.sourceToolCallIds : [];
    lines.push(`This task is to crystallise ONE canvas item into a saved kernel. Item id: ${item.id}.`);
    if (item.caption) lines.push(`Item caption (user-facing label): ${item.caption}`);
    if (ids.length) {
        lines.push("");
        lines.push("The agent tagged this canvas item with the following sourceToolCallIds — these are the tool calls whose output produced this item's data:");
        for (const id of ids) lines.push(`  - ${id}`);
        lines.push("");
        lines.push("Use these as your STARTING POINT: locate each id in the transcript above (each tool entry is prefixed with `[id=…]`). Lift the SUCCESSFUL ones into the kernel's `steps`. Still apply the standard transformations — parameterise literals into `params`, rewrite the render spec's inline `series: [N,N,N]` (if any) to `valueField` / `layerFields` so the kernel projects from row data at run time. If any tagged id points to a FAILED retry or to chat-side state that can't translate (e.g. references a tool call that itself depends on something outside this chain), drop that id and find the right step from the transcript instead.");
    } else {
        lines.push("");
        lines.push("The agent did NOT tag this canvas item with sourceToolCallIds. Identify the producing chain from the transcript yourself:");
        lines.push("  1. Match the canvas item's render spec (kind + field references) against successful analytics_query / execute_python results in the transcript.");
        lines.push("  2. Pick the MOST RECENT successful chain whose final data shape matches the spec's expected input.");
        lines.push("  3. Lift that chain into the kernel's `steps`, applying the standard transformations.");
    }
    // Sibling-item summaries — only when there's more than one item, so
    // the parallel sub-agents don't accidentally crystallise the same
    // chain twice when several items legitimately share steps.
    const siblings = allItems.filter((it) => it && it.id !== item.id);
    if (siblings.length) {
        lines.push("");
        lines.push("Other canvas items being saved in parallel (do not crystallise their recipes here):");
        for (const sib of siblings) {
            const sibIds = Array.isArray(sib.sourceToolCallIds) && sib.sourceToolCallIds.length
                ? ` · tagged: [${sib.sourceToolCallIds.join(", ")}]`
                : "";
            const sibCap = sib.caption ? ` — ${truncate(sib.caption, 60)}` : "";
            lines.push(`  - id=${sib.id} · kind=${sib.spec?.kind || "?"}${sibCap}${sibIds}`);
        }
        lines.push("Two items MAY legitimately share data steps (e.g. same analytics_query, different render). That's fine — both kernels get the same `steps` and differ only in `render`. But don't conflate them: stay focused on the item described above.");
    }
    return lines.join("\n");
}

function deriveCommand(item) {
    if (item?.caption) {
        return item.caption
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/^-+|-+$/g, "")
            .slice(0, 48);
    }
    return null;
}

function deriveTitle(item, idx) {
    if (item?.caption) return truncate(item.caption, 60);
    const kind = item?.spec?.kind || "render";
    return `Canvas ${kind} #${idx + 1}`;
}

// Append `-2`, `-3`, … until we find a command not already taken (both
// in the current batch via `used` and against the persistent kernel
// store).
function uniqueCommand(base, used, tenantId) {
    if (!base) return base;
    if (!used.has(base) && !findKernelInCatalogByCommand(tenantId, base)) return base;
    for (let i = 2; i < 100; i++) {
        const candidate = `${base}-${i}`;
        if (!used.has(candidate) && !findKernelInCatalogByCommand(tenantId, candidate)) return candidate;
    }
    return base;
}

function truncate(s, n) {
    if (typeof s !== "string") s = String(s ?? "");
    return s.length > n ? s.slice(0, n - 1) + "…" : s;
}
